import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarIcon, UserIcon, PinIcon, ShieldIcon, CheckBadgeIcon, ChevronLeftIcon } from "../site/icons.jsx";
import { fmtLong, nights as computeNights } from "../lib/dates.js";
import { groupTaxesAndFees } from "../lib/rateMath.js";
import { useHead } from "../lib/seo.js";
import { NAV, OFFER_TYPE, COMPOUND_TYPE, UNIT_TYPE, PayRecipient, ReturnPolicy, stayDateTime, cancelDateTime, money2 } from "../site/HotelMicrodata.jsx";

const IMAGE_ROOT = "https://images.aloharates.com";
const PAYMENT_SDK_URL = "https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js";
// "live" or "sandbox" — not a credential, a switch. It MUST match the
// environment of the LiteAPI key held by rates.aloharates.com, because the
// secretKey this SDK is handed comes from that worker's prebook call. A live
// publicKey against a sandbox prebook (or the reverse) fails at payment time.
// Override on window for local testing against sandbox.
const LITEAPI_PUBLIC_KEY = window.__LITEAPI_PUBLIC_KEY__ || "live";

// No thousands separators: Hotel Center's price crawler reads the visible
// text of the itemprop="price" spans on this page, and "1234.56" parses
// everywhere while "1,234.56" is locale-ambiguous.
function fmtAmount(n) {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}
function usdMoney(n, currency = "USD") {
  if (n == null || isNaN(n)) return "—";
  const symbol = currency === "USD" ? "$" : "";
  return `${symbol}${fmtAmount(n)}${currency !== "USD" ? " " + currency : ""}`;
}

// Visible money with Microdata: currency symbol carries priceCurrency, the
// number carries price. Used for every line inside the CompoundPriceSpecification.
function PriceText({ amount, currency = "USD" }) {
  return (
    <>
      <span itemProp="priceCurrency" content={currency}>{currency === "USD" ? "$" : ""}</span>
      <span itemProp="price" content={money2(amount)}>{fmtAmount(amount)}</span>
      {currency !== "USD" ? ` ${currency}` : ""}
    </>
  );
}

// One itemised UnitPriceSpecification line with a visible amount.
function PriceLine({ name, componentType, amount, currency, atProperty, hotelName }) {
  return (
    <span itemProp="priceComponent" itemScope itemType={UNIT_TYPE}>
      <meta itemProp="name" content={componentType || name}/>
      {componentType && <meta itemProp="priceComponentType" content={componentType}/>}
      <PriceText amount={amount} currency={currency}/>
      <PayRecipient atProperty={atProperty} hotelName={hotelName}/>
    </span>
  );
}

function useUrl() {
  return useMemo(() => new URLSearchParams(location.search), []);
}

function loadPaymentSdk() {
  return new Promise((resolve, reject) => {
    if (window.LiteAPIPayment) return resolve();
    const existing = document.querySelector(`script[src="${PAYMENT_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = PAYMENT_SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function Field({ label, hint, children, colSpan }) {
  return (
    <label className={`flex flex-col gap-1 ${colSpan === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-medium text-ink-soft">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-ink-muted">{hint}</span>}
    </label>
  );
}

function Input(props) {
  return <input {...props} className={`pill-input px-3 py-2 text-sm ${props.className || ""}`}/>;
}

// Radio card for "who is paying" — a whole tappable block rather than a bare
// radio, so it works on a phone without aiming at a 16px dot.
function PayerChoice({ selected, onSelect, title, copy }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${selected ? "border-ocean-600 ring-1 ring-ocean-600 bg-ocean-50/40" : "border-ink/15 hover:border-ink/30"}`}
    >
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-ocean-600" : "border-ink/25"}`}>
        {selected && <span className="h-2.5 w-2.5 rounded-full bg-ocean-600"/>}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{title}</span>
        <span className="block text-xs text-ink-muted mt-0.5">{copy}</span>
      </span>
    </button>
  );
}

export default function CheckoutPage() {
  const params = useUrl();
  const hotelId  = params.get("hotelId");
  const offerId  = params.get("offerId");
  const hotelName = params.get("hotelName") || "";
  const roomName  = params.get("roomName") || "";
  const refundable = params.get("refundable") === "1";
  const cancelDate = params.get("cancelDate") || "";

  // Traveler counts flow via URL params site-wide. Rendered before the prebook
  // returns so the summary stays populated during the network round-trip; once
  // the prebook lands we cross-check its `adultCount` / `childrenAges` and
  // warn (but keep the URL values) if the supplier priced a different mix —
  // that's how you catch mismatches before /book returns 4002.
  const urlAdults = Math.max(1, Number(params.get("adults")) || 0) || null;
  const urlChildrenAges = (params.get("children") || "")
    .split(",").filter(Boolean).map(Number).filter(n => n >= 0 && n <= 17);

  const [prebook, setPrebook] = useState(null);
  const [prebookErr, setPrebookErr] = useState(null);
  const [loading, setLoading]   = useState(true);

  // `holder` is the lead guest checking in. `booker` is only used when someone
  // is booking on another person's behalf — LiteAPI's payload separates the
  // two (holder = who reserves and pays, guests[] = who stays), so we keep
  // them apart here rather than sending the same person twice.
  const [holder, setHolder] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [booker, setBooker] = useState({ firstName: "", lastName: "", email: "" });
  const [bookingFor, setBookingFor] = useState("self");   // "self" | "other"
  const [remarks, setRemarks] = useState("");
  const [sdkErr, setSdkErr] = useState(null);
  const [sdkRendered, setSdkRendered] = useState(false);
  const paymentRef = useRef(null);
  const sdkInit = useRef(false);

  // Transactional page: keep it out of the index, but leave it crawlable —
  // Hotel Center's price validator lands here via the property page's
  // Reserve button and reads the Microdata total below.
  useHead({ title: "Checkout | Aloha Rates", robots: "noindex, nofollow" });

  // 1) Prebook the offer as soon as the page loads — exactly once per offerId.
  // React StrictMode / dev builds re-run effects, and two concurrent prebooks
  // of the same offerId race at LiteAPI: one returns 200, the other returns
  // "5000 offerId not found". Whichever the page kept was a coin flip.
  const prebooked = useRef("");
  useEffect(() => {
    if (!offerId) { setPrebookErr("Missing offer. Please pick a room again."); setLoading(false); return; }
    if (prebooked.current === offerId) return;
    prebooked.current = offerId;
    (async () => {
      // LiteAPI's SANDBOX answers 5000 "offerId not found" for perfectly valid,
      // unexpired offers (et=10800, so they live 3h) roughly half the time —
      // retrying the same id lands on a pool that knows it. Confirmed not to be
      // ours: same rate on both workers, and unaffected by roomMapping.
      // 4 quick attempts clears ~95% in well under a second. If sandbox is
      // still throwing 5000 after that, surface it rather than spinning.
      const MAX_ATTEMPTS = 4;
      let lastErr = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const r = await fetch("/api/prebook", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ offerId, usePaymentSdk: true })
          });
          const payload = await r.json();
          console.log(`[Checkout] /api/prebook attempt ${attempt}/${MAX_ATTEMPTS}`, payload);
          const err = payload?.error;
          if (r.ok && !err) {
            const data = payload?.data || payload;
            if (!data.prebookId || !data.transactionId || !data.secretKey) {
              setPrebookErr("Missing payment session data from prebook");
            } else {
              if (attempt > 1) console.log(`[Checkout] prebook recovered after ${attempt} attempts`);
              setPrebook(data);
            }
            setLoading(false);
            return;
          }
          lastErr = err?.description || err?.message || `Prebook failed (${r.status})`;
          // Only the offer-lookup miss is worth retrying. Sold out, price
          // changed, bad payload — those are terminal, stop now.
          if (err?.code !== 5000) break;
        } catch (netErr) {
          lastErr = netErr.message || "Network error";
        }
        if (attempt < MAX_ATTEMPTS) await new Promise(res => setTimeout(res, 200));
      }
      setPrebookErr(lastErr || "Prebook failed");
      setLoading(false);
    })();
  }, [offerId]);

  const initSdk = async () => {
    if (sdkInit.current) return;
    try {
      await loadPaymentSdk();
    } catch (err) {
      setSdkErr("Could not load payment form (" + (err?.message || "network error") + ")");
      return;
    }
    if (!window.LiteAPIPayment) {
      setSdkErr("Payment SDK not available on window");
      return;
    }
    try {
      // Occupancy from prebook wins over the URL — the supplier priced this
      // exact mix and /book must match it exactly (LiteAPI 4002 fires when
      // guests.length !== adults + children.length). Fall back to what the
      // user searched with only when the prebook omits the fields.
      const bookAdults = rate?.adultCount ?? urlAdults ?? 2;
      const bookChildrenAges = (rate?.childrenAges?.length ? rate.childrenAges : urlChildrenAges) || [];
      // How many OCCUPANCIES (rooms) this prebook covers. /rates/book wants one
      // guest object per occupancy, so the booking page needs this count — the
      // adult/child numbers below are for display only and must not shape the
      // guests array.
      const occCount = Math.max(1, new Set(
        (prebook.roomTypes || []).flatMap(rt => (rt.rates || []).map(r => r.occupancyNumber ?? 1))
      ).size);
      const bookParams = new URLSearchParams({
        tid: prebook.transactionId,
        pid: prebook.prebookId,
        occ: String(occCount),
        adults: String(bookAdults)
      });
      if (bookChildrenAges.length) bookParams.set("children", bookChildrenAges.join(","));
      const returnUrl = `${location.origin}/booking/?${bookParams.toString()}`;
      new window.LiteAPIPayment({
        publicKey: LITEAPI_PUBLIC_KEY,
        secretKey: prebook.secretKey,
        returnUrl,
        targetElement: "#payment-element",
        appearance: { theme: "flat" },
        options: { business: { name: "Aloha Rates" } }
      }).handlePayment();
      sdkInit.current = true;
      console.log("[Checkout] LiteAPIPayment initialized", { publicKey: LITEAPI_PUBLIC_KEY, prebookId: prebook.prebookId });

      // Watch for the SDK's DOM appearing inside #payment-element so we can
      // drop the "Loading…" placeholder — Stripe injects an <iframe>, LiteAPI
      // wraps other elements. Any child that isn't our placeholder counts.
      const el = document.getElementById("payment-element");
      if (el) {
        const check = () => {
          if (el.querySelector("iframe, form, .LiteAPIPayment, [data-sdk-rendered]")) {
            setSdkRendered(true);
            observer.disconnect();
            return true;
          }
          return false;
        };
        if (check()) return;
        const observer = new MutationObserver(check);
        observer.observe(el, { childList: true, subtree: true });
        // Safety net: give up after 6s of waiting
        setTimeout(() => { observer.disconnect(); setSdkRendered(true); }, 6000);
      }
    } catch (err) {
      console.error("[Checkout] SDK init error", err);
      setSdkErr(err.message || "Payment form failed to initialize");
    }
  };

  // ─── Derived values (all from raw prebook response, not URL params) ───
  //
  // LiteAPI model:
  //   retailRate.total.amount  = the amount charged NOW on the card
  //   taxesAndFees[included=true]  = already inside retailRate.total (informational)
  //   taxesAndFees[included=false] = collected at the property (not on card)
  //
  //   Pay today       = retailRate.total.amount   (a.k.a. prebook.price)
  //   Pay at property = sum of excluded taxes and fees
  //   Trip total      = pay today + pay at property
  const rate = prebook?.roomTypes?.[0]?.rates?.[0] || null;
  const currency = prebook?.currency || "USD";
  const ci = prebook?.checkin || params.get("checkin");
  const co = prebook?.checkout || params.get("checkout");
  const nights = computeNights(ci, co);
  // For the summary panel: prefer prebook-authoritative values, fall back to
  // what the user searched with. Never default silently to 2 — that hides
  // upstream missing-param bugs.
  const adults = rate?.adultCount ?? urlAdults ?? 2;
  const childCount = rate?.childCount ?? rate?.childrenAges?.length ?? urlChildrenAges.length;

  const payToday = Number(prebook?.price ?? rate?.retailRate?.total?.[0]?.amount ?? 0);
  // Collapse duplicate/similar line items: LiteAPI can return "City tax",
  // "TAX", "local city tax" as three rows — merge under a single "Taxes"
  // label so the guest doesn't stare at Tax three times. Shared with the
  // structured-data breakdown so markup and display use identical lines.
  const feeGroups = groupTaxesAndFees(rate?.retailRate?.taxesAndFees);
  const taxesIncluded = feeGroups.filter(t => t.included);
  const taxesExcluded = feeGroups.filter(t => !t.included);
  const includedTotal = taxesIncluded.reduce((s, t) => s + Number(t.amount || 0), 0);
  const payAtProperty = taxesExcluded.reduce((s, t) => s + Number(t.amount || 0), 0);
  const tripTotal = Math.round((payToday + payAtProperty) * 100) / 100;
  // Room subtotal = what's on the card MINUS taxes already inside it
  const roomSubtotal = Math.max(0, payToday - includedTotal);
  const nightly = Math.max(0, roomSubtotal / (nights || 1));

  const emailOk = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
  const forSelf = bookingFor === "self";
  // Booking for someone else: the guest's own email is optional (you often
  // don't have it), and the confirmation goes to whoever is paying.
  const validGuest = holder.firstName.trim() && holder.lastName.trim()
    && (forSelf ? emailOk(holder.email) : (!holder.email.trim() || emailOk(holder.email)));
  const validBooker = forSelf || (booker.firstName.trim() && booker.lastName.trim() && emailOk(booker.email));
  const formValid = !!(validGuest && validBooker);

  // Who LiteAPI should record as the reservation holder, and who is staying.
  const payer = forSelf ? holder : { ...booker, phone: holder.phone };
  const leadGuest = { ...holder, email: holder.email.trim() || payer.email };

  // Persist both sides so /booking/ can rebuild the /rates/book payload after
  // the payment SDK redirects away. Written on every edit rather than at
  // submit time — the SDK navigates the page itself, and a value that only
  // existed in React state would be gone by the time we came back.
  useEffect(() => {
    if (!prebook?.prebookId) return;
    try {
      localStorage.setItem(`alohaBooking:${prebook.prebookId}`, JSON.stringify({
        holder: { firstName: payer.firstName, lastName: payer.lastName, email: payer.email, phone: holder.phone || "" },
        guest: { firstName: leadGuest.firstName, lastName: leadGuest.lastName, email: leadGuest.email, phone: holder.phone || "" },
        bookingFor,
        remarks,
        hotelId, offerId,
        hotelName, roomName,
        savedAt: Date.now()
      }));
    } catch {}
  }, [prebook?.prebookId, holder, booker, bookingFor, remarks, hotelId, offerId, hotelName, roomName]);

  // The payment form is mounted as soon as the quote lands. It used to wait
  // behind a "Continue to payment" step, which hid the card fields until the
  // guest form was filled in and made the page feel like two checkouts.
  useEffect(() => {
    if (prebook && !sdkInit.current) initSdk();
  }, [prebook]);

  const heroPhoto = `${IMAGE_ROOT}/properties/${hotelId}/0.webp`;
  const backHref = hotelId
    ? `/properties/${hotelId}?checkin=${ci || ""}&checkout=${co || ""}&adults=${adults}${urlChildrenAges.length ? `&children=${urlChildrenAges.join(",")}` : ""}`
    : "/";

  return (
    <div className="bg-sand-50 min-h-[calc(100dvh-80px)]" {...NAV.finalStage}>
      <div className="container-page py-6 md:py-10">
        <a href={backHref} className="inline-flex items-center gap-1 text-sm text-ocean-700 hover:text-ocean-800">
          <ChevronLeftIcon size={14}/> Back to property
        </a>
        <h1 className="font-display text-2xl md:text-3xl text-ink mt-2">Confirm and book</h1>
        <p className="prose-body text-sm mt-1">Rates and taxes below are the live prebook quote — this is exactly what the property will charge.</p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">
          {/* ────────── Left: Guest info + Payment ────────── */}
          <div className="space-y-5">
            {loading && (
              <div className="card p-6" {...NAV.loading}><div className="h-3 w-1/3 bg-sand-200 rounded animate-pulse mb-3"/><div className="h-3 w-2/3 bg-sand-200 rounded animate-pulse mb-3"/><div className="h-3 w-1/2 bg-sand-200 rounded animate-pulse"/></div>
            )}
            {prebookErr && !loading && (
              <div className="card p-6 border-l-4 border-sunset-500">
                <div className="font-display text-lg text-ink">This rate is no longer available</div>
                <p className="prose-body text-sm mt-1">{prebookErr}</p>
                <a href={backHref} className="btn-primary mt-4 py-2 px-5 text-sm">Pick another room</a>
              </div>
            )}
            {!loading && !prebookErr && prebook && (
              <>
                {/* Change warnings — prebook can report cancellation / board / price shifts
                    vs. the original offer. Ignore sub-3% price wobbles — LiteAPI often
                    reports 0.5–1.5% deltas from tax rounding / FX drift that mean nothing
                    to the guest. Only surface a meaningful shift. */}
                {(prebook.cancellationChanged || prebook.boardChanged || Math.abs(prebook.priceDifferencePercent || 0) >= 3) && (
                  <div className="card p-4 border-l-4 border-sunset-500 text-sm text-ink-soft space-y-1">
                    <div className="font-semibold text-ink">Rate updated since you searched</div>
                    {prebook.cancellationChanged && <div>Cancellation policy has changed — please review the summary.</div>}
                    {prebook.boardChanged && <div>Meal plan has changed — please review the summary.</div>}
                    {Math.abs(prebook.priceDifferencePercent || 0) >= 3 && (
                      <div>Price {prebook.priceDifferencePercent > 0 ? "went up" : "went down"} by {Math.abs(Math.round(prebook.priceDifferencePercent))}%.</div>
                    )}
                  </div>
                )}

                {/* Cancellation summary — pulled from prebook rate, not URL */}
                {rate && (
                  <div className="card p-5 flex items-start gap-3">
                    <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${rate.cancellationPolicies?.refundableTag === "RFN" ? "bg-ocean-50 text-ocean-700" : "bg-sand-100 text-ink-soft"}`}>
                      {rate.cancellationPolicies?.refundableTag === "RFN" ? <CheckBadgeIcon size={18}/> : <ShieldIcon size={18}/>}
                    </span>
                    <div>
                      <div className="font-display text-base text-ink">
                        {rate.cancellationPolicies?.refundableTag === "RFN" ? "Free cancellation" : "Non-refundable"}
                      </div>
                      <div className="text-sm text-ink-muted">
                        {(rate.cancellationPolicies?.cancelPolicyInfos || []).length > 0
                          ? (rate.cancellationPolicies.cancelPolicyInfos[0].cancelTime
                              ? <>Cancel by {new Date(rate.cancellationPolicies.cancelPolicyInfos[0].cancelTime).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} for a full refund.</>
                              : "Refundable — see property terms for details.")
                          : "This rate can't be cancelled or refunded once booked."}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Who is checking in ── */}
                <div className="card p-5 md:p-6">
                  <h2 className="font-display text-lg text-ink mb-1">Who&apos;s checking in?</h2>
                  <p className="text-xs text-ink-muted mb-4">Lead guest&apos;s name must match a government-issued ID.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="First name">
                      <Input value={holder.firstName} onChange={e => setHolder({ ...holder, firstName: e.target.value })} autoComplete="given-name" required/>
                    </Field>
                    <Field label="Last name">
                      <Input value={holder.lastName} onChange={e => setHolder({ ...holder, lastName: e.target.value })} autoComplete="family-name" required/>
                    </Field>
                    <Field
                      label={forSelf ? "Email" : "Guest email (optional)"}
                      hint={forSelf ? "Booking confirmation goes here." : "Leave blank and we will use your email instead."}
                      colSpan={2}
                    >
                      <Input type="email" value={holder.email} onChange={e => setHolder({ ...holder, email: e.target.value })} autoComplete="email" required={forSelf}/>
                    </Field>
                    <Field label="Phone (optional)" colSpan={2}>
                      <Input type="tel" value={holder.phone} onChange={e => setHolder({ ...holder, phone: e.target.value })} autoComplete="tel"/>
                    </Field>
                    <Field label="Special requests (optional)" hint="Not guaranteed by the property." colSpan={2}>
                      <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2}
                        placeholder="Late check-in? High floor? Crib?"
                        className="rounded-2xl border border-ink/10 bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink-muted focus:border-ocean-400 focus:outline-none focus:ring-2 focus:ring-ocean-200 w-full resize-none"/>
                    </Field>
                  </div>
                </div>

                {/* ── Who is paying ── */}
                <div className="card p-5 md:p-6">
                  <h2 className="font-display text-lg text-ink mb-1">Who is paying for this booking?</h2>
                  <p className="text-xs text-ink-muted mb-4">The reservation is held in the payer&apos;s name.</p>
                  <div role="radiogroup" aria-label="Who is paying for this booking?" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <PayerChoice
                      selected={forSelf} onSelect={() => setBookingFor("self")}
                      title="Myself" copy="I&apos;m the guest checking in above."
                    />
                    <PayerChoice
                      selected={!forSelf} onSelect={() => setBookingFor("other")}
                      title="Someone else" copy="I&apos;m booking on behalf of the guest."
                    />
                  </div>
                  {!forSelf && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-ink/5 pt-4">
                      <Field label="Your first name">
                        <Input value={booker.firstName} onChange={e => setBooker({ ...booker, firstName: e.target.value })} autoComplete="given-name" required/>
                      </Field>
                      <Field label="Your last name">
                        <Input value={booker.lastName} onChange={e => setBooker({ ...booker, lastName: e.target.value })} autoComplete="family-name" required/>
                      </Field>
                      <Field label="Your email" hint="Confirmation and receipt go here." colSpan={2}>
                        <Input type="email" value={booker.email} onChange={e => setBooker({ ...booker, email: e.target.value })} autoComplete="email" required/>
                      </Field>
                    </div>
                  )}
                </div>

                {/* ── Payment ── */}
                <div className="card p-5 md:p-6">
                  <h2 className="font-display text-lg text-ink mb-1">Payment details</h2>
                  <p className="text-xs text-ink-muted mb-4">Card details are handled directly by our payment processor — Aloha Rates never sees them.</p>
                  <div id="payment-element" ref={paymentRef} className="min-h-[260px] rounded-2xl border border-ink/10 bg-white p-2">
                    {!sdkErr && !sdkRendered && <div className="text-sm text-ink-muted p-4">Loading secure payment form…</div>}
                    {sdkErr && <div className="text-sm text-sunset-600 p-4">{sdkErr}</div>}
                  </div>
                </div>

                {/* Our own Complete Booking button. The SDK's own submit is hidden
                    via CSS; we programmatically click it to trigger tokenization
                    + redirect to the returnUrl. */}
                <button
                  onClick={() => {
                    const btn = document.querySelector('#payment-element button, #payment-element [type="submit"]');
                    if (btn) btn.click();
                    else alert("Payment form is still loading — try again in a second.");
                  }}
                  disabled={!formValid || !!sdkErr}
                  className="btn-sunset w-full py-3 text-base disabled:opacity-50"
                >
                  Complete booking · Pay {usdMoney(payToday, currency)}
                </button>
                {!formValid && (
                  <div className="text-[11px] text-ink-muted text-center">
                    Fill in the {forSelf ? "guest" : "guest and payer"} details above to finish.
                  </div>
                )}
                <p className="text-[11px] text-ink-muted text-center">
                  Card is only charged when the property confirms your reservation.
                  {payAtProperty > 0 && ` An additional ${usdMoney(payAtProperty, currency)} in local taxes / resort fees is collected at check-in.`}
                </p>
              </>
            )}
          </div>

          {/* ────────── Right: Sticky price summary — RAW prebook values ────────── */}
          {/* Hotel Center price validation reads this Hotel + LodgingReservation
              scope: identifier, name, dates, occupancy, then ONE makesOffer whose
              CompoundPriceSpecification price is the trip total with every tax
              and fee, itemised below. */}
          {/* Phones get the property and the price first — you should know what
              you are buying before you meet a card field. On lg it moves back
              into the right-hand column beside the form. */}
          <aside className="order-first lg:order-none lg:sticky lg:top-24 space-y-4" itemScope itemType="https://schema.org/Hotel https://schema.org/LodgingReservation">
            <meta itemProp="identifier" content={hotelId || ""}/>
            <div className="card overflow-hidden">
              <div className="aspect-[4/3] bg-sand-100">
                <img src={heroPhoto} alt={hotelName} onError={e => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover"/>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <div className="font-display text-base text-ink leading-snug" itemProp="name">{hotelName || "Your stay"}</div>
                  {(rate?.name || roomName) && <div className="text-xs text-ink-muted mt-0.5">{rate?.name || roomName}</div>}
                  {rate?.boardName && rate.boardName !== "Room Only" && <div className="text-[11px] text-ocean-700 mt-1">{rate.boardName}</div>}
                </div>
                <div className="border-t border-ink/5 pt-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-ink-soft"><CalendarIcon size={14} className="text-ocean-600"/>
                    <span><span itemProp="checkinTime" content={ci || ""}>{ci && fmtLong(ci)}</span> → <span itemProp="checkoutTime" content={co || ""}>{co && fmtLong(co)}</span></span>
                  </div>
                  <div className="flex items-start gap-2 text-ink-soft"><UserIcon size={14} className="text-ocean-600 mt-0.5"/>
                    <span>
                      <span itemProp="numAdults">{adults}</span> adult{adults !== 1 ? "s" : ""}{childCount ? <> · <span itemProp="numChildren">{childCount}</span> child{childCount !== 1 ? "ren" : ""}</> : null}
                      {rate?.childrenAges?.length > 0 && (
                        <span className="block text-[11px] text-ink-muted">Ages {rate.childrenAges.join(", ")}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-ink-soft"><PinIcon size={14} className="text-ocean-600"/>
                    <span>{nights} night{nights !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                {prebook && (
                  <div className="border-t border-ink/5 pt-3 text-sm" itemProp="makesOffer" itemScope itemType={OFFER_TYPE}>
                    <span hidden>
                      <meta itemProp="name" content={rate?.name || roomName || "Room"}/>
                      <meta itemProp="identifier" content={offerId || ""}/>
                      <meta itemProp="checkinTime" content={stayDateTime(ci)}/>
                      <meta itemProp="checkoutTime" content={stayDateTime(co, null, "11:00:00")}/>
                      <meta itemProp="availability" content="https://schema.org/InStock"/>
                      <span itemProp="occupancy" itemScope itemType="https://schema.org/QuantitativeValue"><meta itemProp="value" content={String((Number(adults) || 0) + (Number(childCount) || 0))}/></span>
                      <ReturnPolicy refundable={rate?.cancellationPolicies?.refundableTag === "RFN"} cancelTime={cancelDateTime(rate?.cancellationPolicies?.cancelPolicyInfos?.[0]?.cancelTime)}/>
                    </span>
                  <div className="space-y-3" itemProp="priceSpecification" itemScope itemType={COMPOUND_TYPE}>
                    {/* Room + included taxes (all part of pay-today) */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-ink-soft">
                        <span>Room ({nights} × {usdMoney(nightly, currency)})</span>
                        <span><PriceLine name="Base rate" amount={roomSubtotal} currency={currency} atProperty={false}/></span>
                      </div>
                      {taxesIncluded.map((t, i) => (
                        <div key={"i"+i} className="flex justify-between text-ink-muted text-xs">
                          <span>{t.description}</span>
                          <span><PriceLine name={t.description} componentType={t.componentType} amount={t.amount} currency={currency} atProperty={false}/></span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-ink/5 text-ink font-semibold">
                        <span>Pay today ({currency})</span>
                        <span>{usdMoney(payToday, currency)}</span>
                      </div>
                      <div className="text-[10px] text-ink-muted">Charged to your card at booking.</div>
                    </div>

                    {/* Pay at property section — only if there are excluded fees */}
                    {taxesExcluded.length > 0 && (
                      <div className="space-y-1.5 rounded-2xl bg-sand-100/70 p-3">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Pay at property</div>
                        {taxesExcluded.map((t, i) => (
                          <div key={"e"+i} className="flex justify-between text-ink-soft">
                            <span>{t.description}</span>
                            <span><PriceLine name={t.description} componentType={t.componentType} amount={t.amount} currency={currency} atProperty hotelName={hotelName}/></span>
                          </div>
                        ))}
                        <div className="flex justify-between pt-2 border-t border-ink/10 text-ink font-semibold">
                          <span>Collected at check-in</span>
                          <span>{usdMoney(payAtProperty, currency)}</span>
                        </div>
                        <div className="text-[10px] text-ink-muted">Paid directly to the property when you arrive.</div>
                      </div>
                    )}

                    {/* Total stay price — taxes and fees included. This is the
                        figure Hotel Center's crawler extracts and compares. */}
                    <div className="flex justify-between pt-2 border-t border-ink/10 text-ink font-semibold text-base">
                      <span>Trip total{taxesExcluded.length > 0 ? " (incl. pay at property)" : " (taxes & fees included)"}</span>
                      <span><PriceText amount={tripTotal} currency={currency}/></span>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card p-4 flex items-start gap-3 text-xs text-ink-soft">
              <ShieldIcon size={16} className="text-ocean-600 mt-0.5 shrink-0"/>
              <span>Aloha Rates is an OTA. Bookings are guaranteed by our supplier and confirmed directly with the property.</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
